import { useState, type FormEvent } from 'react';
import { apiErrorText, useTransitionRequestMutation } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { TextArea } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import { Buttons, ErrorText, FieldLabel, Form, Hint } from './parts';

interface Props {
  requestId: string;
  title: string;
  /** Подсказка в поле причины — под вид заявки. */
  placeholder?: string;
  onClose: () => void;
}

/**
 * Вернуть поданную заявку инициатору с причиной (`submitted → rejected`).
 * Без причины автомат возврат не пропустит — поле обязательное.
 */
export function RejectDialog({
  requestId,
  title,
  placeholder = 'Что поправить в заявке',
  onClose,
}: Props) {
  const [transition, { isLoading }] = useTransitionRequestMutation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!reason.trim()) return;
    setError(null);
    try {
      await transition({ id: requestId, action: 'reject', reason: reason.trim() }).unwrap();
      onClose();
    } catch (err) {
      setError(apiErrorText(err, 'Заявку не удалось вернуть.'));
    }
  }

  return (
    <Modal open title="Вернуть заявку" onClose={isLoading ? () => undefined : onClose}>
      <Form onSubmit={(e) => void submit(e)}>
        <Hint>
          «{title}» вернётся автору с вашей причиной — он сможет поправить заявку и подать её снова.
        </Hint>
        <FieldLabel>
          Причина (обязательно)
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={placeholder}
            autoFocus
            required
          />
        </FieldLabel>
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <Buttons>
          <Button type="submit" $variant="danger" disabled={isLoading || !reason.trim()}>
            {isLoading ? 'Возвращаем…' : 'Вернуть'}
          </Button>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
        </Buttons>
      </Form>
    </Modal>
  );
}
